import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, ConfigProvider, Form, Modal, message } from 'antd';
import { RECURSO_EMPLEADOS_GESTIONAR, cambiarEstadoEmpleado, crearEmpleado, editarEmpleado } from '../../api/empleadosApi';
import type { EmpleadoFicha, EmpleadosCatalogos } from '../../api/empleadosApi';
import { usePermissions } from '../../contexts/PermissionContext';
import './EmpleadoForm.css';
import { CamposEmpleado } from './CamposEmpleado';
import { AVISO_BAJA, EstadoEmpleado, PREFIJO_DATOS_GUARDADOS, PREFIJO_ESTADO_GUARDADO, useEstadoEmpleado } from './EstadoEmpleado';
import { errorEmpleado } from './errores';
import type { ErrorEmpleado } from './errores';
import { aAlta, aDatosEmpleado, erroresPorCampo, valoresDesdeFicha } from './reglas';
import type { ValoresEmpleado } from './reglas';
import { VisibilidadPostAlta } from './VisibilidadCopiar';

/** Ancho del modal: cuatro y tres campos por fila sin scroll en un portátil. Ant Design lo limita al viewport (100vw - 32px). */
export const ANCHO_MODAL = 1040;

/**
 * Al abrir, el primer campo recibe el foco. El modal enfoca su propio contenedor al abrirse y se lo quitaría al `autoFocus`,
 * así que se hace cuando termina de abrir; y solo si el foco no está ya dentro del formulario (no se le quita a quien ya escribe).
 */
const enfocarPrimerCampo = (abierto: boolean) => {
  if (!abierto) return;
  const formulario = document.querySelector<HTMLElement>('.empleado-modal .empleado-form');
  if (!formulario || formulario.contains(document.activeElement)) return;
  formulario.querySelector<HTMLElement>('input, textarea, select')?.focus();
};

export const AVISO_EXISTIA = 'Ya existía un empleado activo con ese email. No se ha creado otro: te abrimos su ficha.';

interface Props {
  abierto: boolean;
  catalogos: EmpleadosCatalogos | null;
  /** Con ficha, edita ese empleado; sin ella, da de alta uno nuevo. */
  empleado?: EmpleadoFicha | null;
  onCerrar: () => void;
  /** Hubo cambios en Gescom: refrescar el listado y la ficha. */
  onCambiado: () => void;
  /** Abrir la ficha de ese empleado (tras un alta o si ya existía). */
  onAbrirFicha: (idEmpleado: number) => void;
}

export const EmpleadoFormModal: React.FC<Props> = ({ abierto, catalogos, empleado, onCerrar, onCambiado, onAbrirFicha }) => (
  <Modal
    open={abierto}
    onCancel={onCerrar}
    title={empleado ? 'Editar empleado' : 'Nuevo empleado'}
    width={ANCHO_MODAL}
    className="empleado-modal"
    afterOpenChange={enfocarPrimerCampo}
    style={{ top: 24 }}
    footer={null}
    destroyOnClose
    maskClosable={false}
  >
    {abierto && catalogos && (
      <Contenido
        catalogos={catalogos}
        empleado={empleado ?? null}
        onCerrar={onCerrar}
        onCambiado={onCambiado}
        onAbrirFicha={onAbrirFicha}
      />
    )}
  </Modal>
);

const Contenido: React.FC<Omit<Props, 'abierto' | 'empleado' | 'catalogos'> & { catalogos: EmpleadosCatalogos; empleado: EmpleadoFicha | null }> = ({
  catalogos, empleado, onCerrar, onCambiado, onAbrirFicha,
}) => {
  const [form] = Form.useForm<ValoresEmpleado>();
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<ErrorEmpleado | null>(null);
  // Tras un alta se ofrece copiar la visibilidad de otro empleado; la ficha se abre al cerrar el formulario, sea cual sea la forma.
  const [creadoId, setCreadoId] = useState<number | null>(null);
  const fichaPendiente = useRef<number | null>(null);
  useEffect(() => () => {
    if (fichaPendiente.current !== null) onAbrirFicha(fichaPendiente.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const { hasPermission } = usePermissions();
  const estado = useEstadoEmpleado(empleado?.idEmpleado, !!empleado && hasPermission(RECURSO_EMPLEADOS_GESTIONAR));
  // De baja, Gescom rechaza editar los datos (51406): reactivar manda primero el estado; sin reactivar, solo se guarda el estado.
  const enBaja = !!empleado && (estado.inicial ? !estado.inicial.activo : !empleado.activo);
  const reactiva = enBaja && !!estado.valor?.activo;
  const soloEstado = enBaja && !reactiva;

  const mostrarErrorEstado = (e: unknown, prefijo?: string) => {
    const er = errorEmpleado(e);
    setError(prefijo ? { ...er, mensaje: `${prefijo} ${er.mensaje}` } : er);
  };
  // Lo que tiene campo se pinta en su campo; lo que no, en el aviso del formulario.
  const mostrarErrorDatos = (e: unknown, prefijo?: string) => {
    const { campos, resto } = erroresPorCampo(e);
    if (!campos.length) {
      const er = errorEmpleado(e);
      setError(prefijo ? { ...er, mensaje: `${prefijo} ${er.mensaje}` } : er);
      return;
    }
    const porCampo = new Map<keyof ValoresEmpleado, string[]>();
    campos.forEach((c) => porCampo.set(c.campo, [...(porCampo.get(c.campo) ?? []), c.mensaje]));
    form.setFields(Array.from(porCampo, ([name, errors]) => ({ name, errors })));
    form.scrollToField(campos[0].campo, { focus: true });
    const faltas = resto.length ? resto : undefined;
    setError(prefijo ? { mensaje: `${prefijo} revisa los campos marcados.`, faltas } : resto.length ? { mensaje: 'Revisa los datos introducidos.', faltas: resto } : null);
  };

  const terminarEdicion = () => {
    message.success('Cambios guardados.');
    onCambiado();
    onCerrar();
  };

  // Lo llama el formulario al enviarlo (botón o Enter), ya validado campo a campo.
  const guardar = async (v: ValoresEmpleado) => {
    setGuardando(true);
    setError(null);
    try {
      if (empleado) {
        const id = empleado.idEmpleado;
        const enviarEstado = async () => {
          if (!estado.cambio) return;
          await cambiarEstadoEmpleado(id, estado.cambio);
          estado.marcarGuardado();
        };
        if (enBaja) {
          try {
            await enviarEstado();
          } catch (e) {
            mostrarErrorEstado(e);
            return;
          }
          if (reactiva) {
            try {
              await editarEmpleado(id, aDatosEmpleado(v));
            } catch (e) {
              onCambiado();
              mostrarErrorDatos(e, PREFIJO_ESTADO_GUARDADO);
              return;
            }
          }
          terminarEdicion();
          return;
        }
        try {
          await editarEmpleado(id, aDatosEmpleado(v));
        } catch (e) {
          mostrarErrorDatos(e);
          return;
        }
        try {
          await enviarEstado();
        } catch (e) {
          // Los datos ya están guardados: se refresca el listado y el formulario sigue abierto con el motivo.
          onCambiado();
          mostrarErrorEstado(e, PREFIJO_DATOS_GUARDADOS);
          return;
        }
        terminarEdicion();
        return;
      }
      const alta = await crearEmpleado(aAlta(v));
      if (!alta.creado) {
        message.warning(AVISO_EXISTIA, 6);
        onCerrar();
        onAbrirFicha(alta.idEmpleado);
        return;
      }
      onCambiado();
      message.success('Empleado creado.');
      fichaPendiente.current = alta.idEmpleado;
      setCreadoId(alta.idEmpleado);
    } catch (e) {
      mostrarErrorDatos(e);
    } finally {
      setGuardando(false);
    }
  };

  if (creadoId !== null) return <VisibilidadPostAlta idEmpleado={creadoId} onAbrirFicha={onCerrar} />;

  return (
    <Form
      form={form}
      layout="vertical"
      className="empleado-form"
      onFinish={guardar}
      validateTrigger={['onChange', 'onBlur']}
      scrollToFirstError={{ focus: true }}
      size="small"
      initialValues={empleado ? valoresDesdeFicha(empleado) : { accesoGdgo: false, asignarCcl: false, controlAcompaniamiento: false }}
    >
      <div className="empleado-form__cuerpo">
      {soloEstado && <p className="empleado-estado__nota">{AVISO_BAJA}</p>}
      <ConfigProvider componentDisabled={soloEstado}>
        <CamposEmpleado form={form} catalogos={catalogos} conFechaAlta={!empleado} interlocucionInicial={empleado?.nivelInterlocucion} />
      </ConfigProvider>
      {empleado && <EstadoEmpleado estado={estado} idEmpleado={empleado.idEmpleado} />}

      {error && (
        <Alert
          type="error"
          showIcon
          message={error.mensaje}
          description={error.faltas?.length ? <ul style={{ paddingLeft: 18, margin: 0 }}>{error.faltas.map((f) => <li key={f}>{f}</li>)}</ul> : undefined}
        />
      )}
      </div>
      <div className="empleado-form__pie">
        <Button onClick={onCerrar}>Cancelar</Button>
        <Button type="primary" htmlType={soloEstado ? 'button' : 'submit'} loading={guardando} onClick={soloEstado ? () => guardar(form.getFieldsValue()) : undefined}>{empleado ? 'Guardar cambios' : 'Crear empleado'}</Button>
      </div>
    </Form>
  );
};
